import moment from 'moment-timezone';
import {distinctUntilChanged, finalize, map, takeUntil} from 'rxjs/operators';
import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AceValidators} from '@shared/util/validators/ace-validators';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {Equipment} from '@app/graphql-typings/graphql';
import {DateTimeSelectComponentErrorKey} from '@shared/components/date-time-select/date-time-select.component';
import {SiteSelectComponent} from '@app/shared/components/site-select/site-select.component';
import {TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {actionStatuses, AlertTicket, EscalatedTicket, EscalatedTicketCreateBody, EscalatedTicketUpdateBody, MessageTranslateService, resolvedTicketStatusIds, SiteService, Ticket, TicketLinkDirection, TicketPriorityId, TicketStatus, TicketStatusId, TicketType} from '@shared/services';
import {TicketUnassignedOption, TicketUpsertData, TicketUpsertForm, UNASSIGNED_ID} from '@app/layout/sites/tickets/tickets.interface';
import {TicketClearableResult} from '@app/layout/sites/tickets/ticket-select/ticket-select.component';
import {Subject} from 'rxjs';
import {FeatureService} from "@shared/services/feature/feature-service";


enum SelectTimestampField {
  SELECT_CREATED_AT = 'select_created_at',
  SELECT_RESOLVED_AT = 'select_resolved_at',
}


enum TimestampField {
  CREATED_AT = 'created_at',
  RESOLVED_AT = 'resolved_at',
}


@Component({
  selector: 'app-escalated-ticket-upsert',
  templateUrl: './escalated-ticket-upsert.component.html',
  styleUrls: ['./escalated-ticket-upsert.component.scss'],
  standalone: false
})
export class EscalatedTicketUpsertComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy, TicketUpsertForm {

  @ViewChild(SiteSelectComponent)
  private siteInput: SiteSelectComponent;

  @ViewChild('titleInput')
  private titleInput: ElementRef<HTMLInputElement>;

  @Input()
  site: SiteInfo | null;

  @Input()
  ticket: EscalatedTicket | null;

  @Output()
  ticketUpsertData = new EventEmitter<TicketUpsertData | null>();

  @Output()
  loading = new EventEmitter<boolean>();

  @Output()
  pending = new EventEmitter<boolean>();

  TicketType = TicketType;

  now: moment.Moment;
  minResolvedTs: moment.Moment = null;
  maxCreatedTs: moment.Moment = null;
  equipmentLoading = false;
  assigneesLoading = false;
  assigneesById: Map<string, User>;
  readonly UNASSIGNED_ID = UNASSIGNED_ID;

  private readonly DEFAULT_TICKET_PRIORITY_ID = TicketPriorityId.PRIORITY_P3_ROUTINE;
  private readonly UNASSIGNED_OPTION: TicketUnassignedOption = {
    id: UNASSIGNED_ID,
    fullName: this.translateService.instant('Unassigned')
  };
  private readonly ngUnsubscribe = new Subject<void>();

  // keep track of all tickets related to the current context
  // will be used when upsert current action
  // by doing so we make sure only necessary tickets that could
  // potentially change are captured by the nearest caller of the upsert methods
  // no need to rely on external reference provider.
  private ticketsById: Map<string, Ticket> = new Map();
  private mandatorySourceTicketIds: Set<string> = new Set();

  assigneeOptions = [this.UNASSIGNED_OPTION];
  equipmentOptions: Equipment[];
  statusOptions: TicketStatus[];

  ticketForm: UntypedFormGroup;

  createdAtTranslatedErrorMessages = {
    [DateTimeSelectComponentErrorKey.AFTER_MAX_DATE]: { messageKey: 'Created Date should be before or the same as the Resolved Date' }
  };

  resolvedAtTranslatedErrorMessages = {
    [DateTimeSelectComponentErrorKey.AFTER_MAX_DATE]: { messageKey: 'Resolved Date cannot not be in the future' },
    [DateTimeSelectComponentErrorKey.BEFORE_MIN_DATE]: { messageKey: 'Resolved Date should be after or the same as the Created Date' }
  };
  sitesById: Map<number, SiteInfo>;
  showTimeTaken = false;

  constructor(
      private cdRef: ChangeDetectorRef,
      private formBuilder: UntypedFormBuilder,
      private translateService: MessageTranslateService,
      private ticketDataCaches: TicketDataCaches,
      private siteService: SiteService,
      private featureService: FeatureService
  ) {
    this.now = moment.utc();
    this.maxCreatedTs = moment(this.now);
    this.minResolvedTs = moment(this.now);
    this.initForm();
  }

  private static momentEqual(m1: moment.Moment, m2: moment.Moment): boolean {
    return (m1 == null && m2 == null) || (m1 != null && m1.isSame(m2));
  }

  ngOnInit(): void {
    this.statusOptions = actionStatuses;
    this.siteService.getCachedSiteInfosById().subscribe(sitesById => {
      this.sitesById = sitesById;
      const selectedSiteId = this.ticketForm?.value?.site_id;
      if (selectedSiteId != null) {
        this.updateShowTimeTaken(this.sitesById.get(selectedSiteId));
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.ticket) {
      this.validateTicketAndSiteInput(this.ticket, this.site);
      this.updateForm(this.ticket);
    } else if (changes?.site) {
      this.validateTicketAndSiteInput(this.ticket, this.site);
      this.updateShowTimeTaken(this.site);
      if (this.ticket?.ticket_id == null) {
        this.siteIdControl.setValue(this.site?.site_id);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.showSiteControl && this.siteIdControl.value == null) {
      this.siteInput?.focus();
    } else if (this.ticketForm?.get('summary')?.value == null) {
      this.titleInput?.nativeElement?.focus();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  isResolvedStatusId(statusId: TicketStatusId): boolean {
    return resolvedTicketStatusIds.has(statusId);
  }

  showErrors() {
    Object.values(this.ticketForm.controls).forEach((control) => {
      if (control.errors && !control.touched) {
        control.markAsTouched();
      }
    });
  }

  private reloadEquipment(siteId: number, updateFieldValue: boolean = false): void {
    if (siteId != null) {
      this.setEquipmentLoading(true);
      this.ticketDataCaches.getEquipmentBySiteId(siteId)
        .pipe(finalize(() => this.setEquipmentLoading(false)))
        .subscribe(equipmentById =>
          this.equipmentOptions = equipmentById ? Array.from(equipmentById.values()) : null);
    } else {
      this.equipmentOptions = null;
    }
    if (updateFieldValue) {
      this.equipmentIdsControl.reset();
    }
  }

  private reloadAssignees(siteId: number, updateFieldValue: boolean = false): void {
    if (siteId != null) {
      this.setAssigneesLoading(true);
      this.ticketDataCaches.getAssigneesBySiteId(siteId)
        .pipe(finalize(() => this.setAssigneesLoading(false)))
        .subscribe(assigneesByIdResponse => {
          // Need to cater for a ticket being assigned to a user who is no longer associated with the site. Ensure that
          // the current assignee is included in the list of possible assignees
          const assigneesById = new Map(assigneesByIdResponse);
          this.ticket?.assignees?.forEach(assignee => {
            if (assignee && !assigneesById.has(assignee.id)) {
              assigneesById.set(assignee.id, assignee);
            }
          });
          this.assigneesById = assigneesById;
          this.assigneeOptions = [this.UNASSIGNED_OPTION, ...this.assigneesById.values()];

          if (updateFieldValue && assigneesById.get(this.assigneeIdControl.value) == null) {
            this.assigneeIdControl.reset(this.UNASSIGNED_ID);
          }
        });
    } else {
      this.assigneesById = null;
      this.assigneeOptions = [this.UNASSIGNED_OPTION];
      if (updateFieldValue) {
        this.assigneeIdControl.reset(null);
      }
    }
  }

  private setAssigneesLoading(assigneesLoading: boolean): void {
    if (this.assigneesLoading !== assigneesLoading) {
      this.assigneesLoading = assigneesLoading;
      this.fireLoadingEvent();
    }
  }

  private setEquipmentLoading(equipmentLoading: boolean): void {
    if (this.equipmentLoading !== equipmentLoading) {
      this.equipmentLoading = equipmentLoading;
      this.fireLoadingEvent();
    }
  }

  onAlertsLoaded(alerts: Map<string, Ticket>) {
    alerts?.forEach(alert => TicketUtils.registerTicketInMap(alert, this.ticketsById));
  }

  getCanClearLinkedAlertFn(): (ticket: Ticket) => TicketClearableResult {
    return function (alert: Ticket) {
      const nonClearable = this.mandatorySourceTicketIds.has(alert.ticket_id);
      return {
        nonClearable,
        ...nonClearable
            ? { reason: { messageKey: 'Cannot remove alert, as the action is being created for this alert' } }
            : null,
      };
    }.bind(this);
  }

  groupByFn(assignee: User): string | null {
    const entity = assignee.entity;
    return entity ? entity.name : null;
  }

  get linkedAlerts(): AlertTicket[] | null {
    return this.ticketForm.get('linked_alerts').value;
  }

  keyPressNumbersWithDecimal(event): boolean {
    const charCode = event.keyCode;
    if (charCode != 45 && charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  get selectedSite(): SiteInfo {
    const siteId = this.siteIdControl.value;
    return this.site && this.site.site_id === siteId ? this.site : this.sitesById?.get(siteId);
  }

  get showSiteControl(): boolean {
    return this.site == null && this.ticket?.ticket_id == null;
  }

  private setupSiteChangeHandler(): void {
    this.siteIdControl.valueChanges.subscribe(siteId => {
      this.setupSiteDependentFields(siteId, true);
    });
  }

  private setupSiteDependentFields(siteId: number, updateFieldValue: boolean = false): void {
    this.enableDisableSiteDependentFields(siteId != null);
    this.reloadAssignees(siteId, updateFieldValue);
    this.reloadEquipment(siteId, updateFieldValue);
    if (updateFieldValue) {
      this.linkedAlertsControl.reset();
    }
  }

  private enableDisableSiteDependentFields(enable: boolean): void {
    if (enable) {
      this.assigneeIdControl.enable();
      this.equipmentIdsControl.enable();
      this.linkedAlertsControl.enable();
    } else {
      this.assigneeIdControl.disable();
      this.equipmentIdsControl.disable();
      this.linkedAlertsControl.disable();
    }
  }

  private get siteIdControl(): AbstractControl<number> {
    return this.ticketForm.controls.site_id;
  }

  private get assigneeIdControl(): AbstractControl<string> {
    return this.ticketForm.controls.assignee_id;
  }

  private get equipmentIdsControl(): AbstractControl<number[]> {
    return this.ticketForm.controls.equipment_ids;
  }

  private get linkedAlertsControl(): AbstractControl<AlertTicket[]> {
    return this.ticketForm.controls.linked_alerts;
  }

  private setupChangeHandlerForSelectTimestampFields(): void {
    this.ticketForm.get(SelectTimestampField.SELECT_CREATED_AT).valueChanges.subscribe((selectCreatedAt: boolean) => {
      const createdAtControl = this.ticketForm.get(TimestampField.CREATED_AT);
      const selectResolvedAtControl = this.ticketForm.get(SelectTimestampField.SELECT_RESOLVED_AT);
      if (selectCreatedAt) {
        createdAtControl.enable();
        selectResolvedAtControl.enable();
      } else {
        // If created_at is set to be now there's no point showing resolved at picker, resolved at should default to now,
        // and radio button should be disabled to avoid overriding
        selectResolvedAtControl.setValue(false);
        selectResolvedAtControl.disable();
        createdAtControl.disable();
      }
    });

    this.ticketForm.get(SelectTimestampField.SELECT_RESOLVED_AT).valueChanges.subscribe((selectResolvedAt: boolean) => {
      const resolvedAtControl = this.ticketForm.get(TimestampField.RESOLVED_AT);
      if (selectResolvedAt) {
        resolvedAtControl.enable();
      } else {
        resolvedAtControl.disable();
      }
    });

    this.ticketForm.get(TimestampField.CREATED_AT).valueChanges.pipe(distinctUntilChanged(EscalatedTicketUpsertComponent.momentEqual))
        .subscribe(value => {
          this.minResolvedTs = value ? moment.min(moment(value), this.now) : this.now;
          this.cdRef.detectChanges();
        });

    this.ticketForm.get(TimestampField.RESOLVED_AT).valueChanges.pipe(distinctUntilChanged(EscalatedTicketUpsertComponent.momentEqual))
        .subscribe(value => {
          this.maxCreatedTs = value ? moment.min(moment(value), this.now) : this.now;
          this.cdRef.detectChanges();
        });
  }

  private getTicketCreateBody(): EscalatedTicketCreateBody {
    const {
      site_id, summary, status_id, assignee_id, description, equipment_ids, solution, priority_id, benefit_id, savings,
      select_created_at, select_resolved_at, created_at, resolved_at, time_taken
    } = this.ticketForm.value;

    const ticketCreateBody: EscalatedTicketCreateBody = {
      type: TicketType.ESCALATED,
      site_id,
      assignee_ids: (assignee_id && assignee_id !== UNASSIGNED_ID) ? [assignee_id] : null,
      equipment_ids,
      summary,
      status_id,
      description,
      solution,
      priority_id,
      benefit_id,
      time_taken,
      savings: savings ? Number(savings) : null, // Use truthy comparison to ensure '' is converted to null
      source_ticket_ids: this.linkedAlerts?.map(ticket => ticket.ticket_id)
    };

    if (select_created_at) {
      ticketCreateBody.created_at = moment.utc(created_at);
    }

    if (select_resolved_at && this.isResolvedStatusId(status_id)) {
      // only send resolved_at when current status is resolved status
      // and also timestamp has been specified
      // otherwise it should be programmatically worked out by backend
      ticketCreateBody.resolved_at = moment.utc(resolved_at);
    }

    return ticketCreateBody;
  }

  private getTicketUpdateBody(): EscalatedTicketUpdateBody {
    const updatedTicketBody = this.getTicketCreateBody();
    return {
      ticket_id: this.ticket.ticket_id,
      ...updatedTicketBody,
      created_at: updatedTicketBody.created_at,
      ticket_archived: this.ticket.ticket_archived
    };
  }

  private updateShowTimeTaken(site: SiteInfo | null) {
    this.showTimeTaken = site ? this.featureService.timeTakenEnabled(site) : false;
  }

  private initForm(): void {
    this.ticketForm = this.formBuilder.group({
      site_id: [null, Validators.required],
      summary: [null, Validators.required],
      status_id: [TicketStatusId.STATUS_NEW, Validators.required],
      assignee_id: null,
      description: [null, Validators.required],
      equipment_ids: null,
      solution: null,
      priority_id: [this.DEFAULT_TICKET_PRIORITY_ID],
      benefit_id: null,
      savings: [null, [AceValidators.number(), AceValidators.min(-10000000000), AceValidators.max(10000000000)]],
      time_taken: null,
      linked_alerts: null,
      [SelectTimestampField.SELECT_CREATED_AT]: false,
      [SelectTimestampField.SELECT_RESOLVED_AT]: [{ value: false, disabled: true }],
      [TimestampField.CREATED_AT]: [{ value: this.now, disabled: true }, Validators.required],
      [TimestampField.RESOLVED_AT]: [{ value: this.now, disabled: true }, Validators.required],
    });

    this.setupChangeHandlerForSelectTimestampFields();
    this.setupSiteChangeHandler();

    this.setupSiteDependentFields(null);

    this.ticketForm.valueChanges.subscribe(value => {
      const create = !this.ticket?.ticket_id;
      const emitValue = this.ticketForm.valid && (create || this.ticketForm.dirty);
      const site = this.site != null && this.site.site_id === value?.site_id ? this.site : (this.sitesById?.get(value?.site_id) ?? null);
      this.updateShowTimeTaken(site);
      const ticketUpsertData = !emitValue ? null : {
        site,
        assigneesById: this.assigneesById,
        linkedAlerts: value?.linked_alerts ?? null,
        ticketBody: create ? this.getTicketCreateBody() : this.getTicketUpdateBody()
      };
      this.ticketUpsertData.emit(ticketUpsertData);
    });

    this.ticketForm.statusChanges.pipe(
      map(() => this.ticketForm.pending),
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(pending => this.pending.emit(pending));
  }

  private updateForm(ticket: EscalatedTicket): void {
    const {
      ticket_id, summary, status, assignees, description, equipment_ids, solution, priority, benefit, created_at, resolved_at, ticket_links, savings, time_taken,
      site_id
    } = ticket || {};

    const isNewTicket = !ticket_id;

    if (ticket_id) {
      this.ticketsById.set(ticket_id, ticket);
    }

    const linkedAlerts = [];
    ticket_links?.forEach(({ direction, ticket }) => {
      if (direction === TicketLinkDirection.TO_SOURCE) {
        linkedAlerts.push(<AlertTicket>ticket);
        this.ticketsById.set(ticket.ticket_id, ticket);
        if (isNewTicket) {
          this.mandatorySourceTicketIds.add(ticket.ticket_id);
        }
      }
    });

    const siteId = this.site?.site_id ?? site_id ?? null;
    this.ticketForm.reset({
      site_id: siteId,
      summary: summary,
      status_id: status ? status.status_id : TicketStatusId.STATUS_NEW,
      assignee_id: assignees?.length ? assignees[0].id : siteId ? this.UNASSIGNED_OPTION.id : null,
      description: description,
      equipment_ids: equipment_ids?.length ? equipment_ids : null,
      solution: solution,
      priority_id: priority ? priority.priority_id : (isNewTicket ? this.DEFAULT_TICKET_PRIORITY_ID : TicketPriorityId.PRIORITY_NONE),
      benefit_id: benefit?.length ? benefit[0].benefit_id : null,
      savings: savings != null ? savings.toString() : null,
      time_taken,
      linked_alerts: linkedAlerts?.length ? linkedAlerts : null,

      // when ticket has created_at, radio should be set to "select timestamp"
      [SelectTimestampField.SELECT_CREATED_AT]: !!created_at,

      // when ticket has resolved_at, radio should be set to "select timestamp"
      [SelectTimestampField.SELECT_RESOLVED_AT]: !!resolved_at,

      // when ticket does not have created_at, radio will be set to "now", and created_at date time picker would be disabled
      [TimestampField.CREATED_AT]: created_at || this.now,

      // when ticket does not have resolved_at, radio will be set to "now", and resolved_at date time picker would be disabled
      [TimestampField.RESOLVED_AT]: resolved_at || this.now,
    }, {emitEvent: false});

    const selectResolvedAt = this.ticketForm.get(SelectTimestampField.SELECT_RESOLVED_AT);
    const createdAt = this.ticketForm.get(TimestampField.CREATED_AT);
    const resolvedAt = this.ticketForm.get(TimestampField.RESOLVED_AT);
    if (!created_at) {
      selectResolvedAt.disable();
      createdAt.disable();
    } else {
      selectResolvedAt.enable();
      createdAt.enable();
    }
    if (!resolved_at) {
      resolvedAt.disable();
    } else {
      resolvedAt.enable();
    }

    this.setupSiteDependentFields(this.siteIdControl.value);
  }

  private validateTicketAndSiteInput(ticket: EscalatedTicket, site: SiteInfo): void {
    if (site && ticket && ticket.ticket_id != null && ticket.site_id !== site.site_id) {
      throw new Error('Ticket with ticket id: "' + ticket.ticket_id + '" and site id: "'
        + ticket.site_id + '", does not belong to site with site id: "' + site.site_id + '"');
    }
  }

  private fireLoadingEvent(): void {
    this.loading.emit(this.assigneesLoading || this.equipmentLoading);
  }
}
